/**
 * Свёрнутый список блока «Голоса клиента» (#2060): первые `limit` строк и
 * «Показать все N». Правила — в `VoiceTopList.tsx`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { collapsedRows, useShownKeys, type VoiceTopListProps } from './voice-top-list';

export function VoiceFoldedList<T>({
  rows,
  limit,
  rowKey,
  calls = () => 1,
  revealKeys = [],
  renderRow,
  onShownKeys,
  list = 'div',
  gap = 'space-y-2',
}: VoiceTopListProps<T>) {
  const { t } = useTranslation('dashboard');
  const collapsed = limit === undefined ? rows : collapsedRows(rows, limit, calls);
  // A row to reveal (opened from the address, or the selected theme), beyond
  // the collapsed rows: on mount (back from a call) or later (a view switch
  // moved it down). The list unfolds when such a row becomes hidden, not on
  // every render — a viewer may fold it again. Which row is hidden is what
  // counts: another theme selected below the five unfolds a folded list again.
  const hidden =
    revealKeys.find(
      (key): key is string =>
        key != null &&
        !collapsed.some((row) => rowKey(row) === key) &&
        rows.some((row) => rowKey(row) === key)
    ) ?? null;
  const [showAll, setShowAll] = useState(hidden !== null);
  const [wasHidden, setWasHidden] = useState(hidden);
  if (hidden !== wasHidden) {
    setWasHidden(hidden);
    if (hidden !== null) setShowAll(true);
  }
  const folds = collapsed.length > 0 && collapsed.length < rows.length;
  const visible = showAll || !folds ? rows : collapsed;
  useShownKeys(visible, rowKey, onShownKeys);
  const List = list;

  return (
    <>
      <List className={gap}>{visible.map(renderRow)}</List>
      {folds && (
        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showAll}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? t('voicePage.layers.showFewer', { count: collapsed.length })
              : t('voicePage.layers.showAll', { count: rows.length })}
          </Button>
        </div>
      )}
    </>
  );
}
